'use strict'

import { observable, action, toJS } from 'mobx'
import axios from 'axios'
import moment from 'moment'
import { countTotalTime, getTotalProductionTime, formatNumber } from '../../../helpers'
import DashStore from '../../dashboard/Store/DashStore'

moment.locale('sv')

class Store {
    // Scroll-top position
    @observable scrollTop = 0
    // Indicate loading screen
    @observable loading = true
    // Main user data
    @observable user = {}
    // Main object for fetched orders
    @observable ordersByDate = {}
    // Options for filters
    @observable filters = {
        filterLoading: true,
        selects: {
            articles: {
                loadingText: 'Laddar ...',
                placeholder: 'Välj benämning',
                options: [],
                selected: null
            },
            owners: {
                loadingText: 'Laddar ...',
                placeholder: 'Välj ägare',
                options: [{
                    id: null,
                    label: 'Välj alla',
                    value: 'Välj alla',
                    type: 'owners',
                }],
                selected: null
            },
            departments: {
                loadingText: 'Laddar ...',
                placeholder: 'Välj avdelning',
                options: [{
                    id: null,
                    label: 'Välj alla',
                    value: 'Välj alla',
                    type: 'departments',
                }],
                selected: null            
            },
            groupBy: {
                loadingText: 'Laddar ...',
                placeholder: 'Gruppera på',
                options: [],
                selected: null
            }          
        }
    }
    @observable popup = {
        show: false,
        data: {
            name: '',
            phone: '',
            cellphone: '',
            email: '',
            role: '',
            note: ''
        }
    }
    @observable popupChangeDate = {
        show: false,
        data: {
            order: '',
            locked: true,
            notify_delivery_date_change: false,
            delivery_change_reason: ''
        }
    }
    @observable productionDate = null
    @observable deliveryDate = null
    @observable taskWarning = false
    @observable popupOrder = {
        show: false,
        paused: false,
        showComment: false
    }
    @observable taskChanged = {
        date: null,
        orderPosition: null,
        taskPostion: null
    }
    // Remember array for filters
    @observable selectedFilters = []
    // Filter summary
    @observable FilterArticleSummary = {}
    // summary for all orders
    @observable orderSummation = {}
    @observable dateRange = {
        from: null,
        to: null,
        defaultRange: {},
        type: ''
    }
    // Ajax interval reset comment field so we need to save it here
    @observable currentComment = ''
    // Remember array for opened days
    openedDays = []
    // Remember array for opened orders
    openedOrders = []
    // Temp holder for Articles filters
    filterTemp = [{
        article_number: null,
        id: null,
        label: 'Välj alla',
        value: 'Välj alla',
        type: 'articles',
    }]
    ordersPayload = {
        filters: [],
        range: this.dateRange
    }
    ajaxFirstReq = true
    closestDate = null
    openSetting = null
    @observable totalCompletedTimeToday = 0

    constructor() {        
        /**
         * This will only start waiting once all the requests are finished
         * preventing them from stacking up
         */
        (async () => {

            if (window.location.pathname.indexOf('/production') == 0) {
                await this.FETCH_FILTERS_ASYNC()

                while (true) {
                    await this.FETCH_ORDERS_ASYNC(this.filters.selects.groupBy.selected)
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                }
            }
        })()
    }

    @action updateScrollPosTop = (top) => {
        this.scrollTop = top
    }

    @action HANDLE_SET_RANGE_DEFAULT = (type, run) => {
        const today = moment()
        const target = moment().add(1, type)

        this.dateRange = {
            from: {
                year: parseFloat(today.format('YYYY')),
                month: parseFloat(today.format('MM')),
                day: parseFloat(today.format('DD'))
            },
            to: {
                year: parseFloat(target.format('YYYY')),
                month: parseFloat(target.format('MM')),
                day: parseFloat(target.format('DD'))
            }
        }

        this.dateRange.type = type
        this.ordersPayload.range.from = this.dateRange.from
        this.ordersPayload.range.to = this.dateRange.to

        if (run) {
            this.FETCH_ORDERS_ASYNC()
        }
    }

    @action FETCH_FILTERS_ASYNC = async () => {
        await axios.get('/production/fetch-filters').then((response) => {
            if (response.status === 200) {
                const data = response.data

                for (const filter in data) {
                    data[filter].forEach((item) => {
                        let label;
                        let value;

                        if (filter == 'owner') {
                            label = `${ item.firstname } ${ item.lastname }`
                            value = `${ item.firstname } ${ item.lastname }`
                        } else if (filter == 'groupBy') {
                            label = item.name;
                            value = item.value;
                        } else {
                            label = item.name;
                            value = item.name;
                        }

                        const obj = {
                            id: item.id,
                            label: label,
                            value: value,
                            type: filter
                        }

                        this.filters.selects[filter].options.push(obj)

                        if (item.preSelect) {
                            this.filters.selects[filter].selected = obj;
                        }
                    })
                }

                this.filters.filterLoading = false
            }
        })
    }

    @action FETCH_ORDERS_ASYNC = async (filter) => {

        if (this.popupChangeDate && this.popupChangeDate.show) {
            return;
        }
        
        if (filter !== undefined) {
            
            const currentIndexType = this.selectedFilters.findIndex(f => f.type === filter.type)
            const filterPayloadIndex = this.ordersPayload.filters.findIndex(f => f.type === filter.type)
            
            // Set selected based on type.
            this.filters.selects[filter.type].selected = filter
            
            if (filter.value !== 'Välj alla') {
                currentIndexType !== -1 ? this.selectedFilters[currentIndexType] = filter : this.selectedFilters.push(filter)
                filterPayloadIndex !== -1 ? this.ordersPayload.filters[filterPayloadIndex] = filter : this.ordersPayload.filters.push(filter)
            } else {
                this.ordersPayload.filters.splice(filterPayloadIndex, 1)
            }
        }

        await axios.post('/production/orders', this.ordersPayload).then((response) => {
            if (response.status === 200) {
                // Save scrollY pos
                this.scrollTop = window.scrollY
                // Settings
                this.openSetting = response.data.user.openSetting

                this._setOrders(response.data.body.orders)                     
                this.FilterArticleSummary.amount = response.data.body.length

                // Summary data
                if (response.data.body.articleSummary) {
                    this.FilterArticleSummary.unit = response.data.body.articleSummary.unit !== '' ? response.data.body.articleSummary.unit : ''
                    this.FilterArticleSummary.amount = response.data.body.articleSummary.amount ? response.data.body.articleSummary.amount : null
    
                    if (response.data.body.articleSummary.unit !== 'st') {
                        this.FilterArticleSummary.amount = response.data.body.articleSummary.amount ? response.data.body.articleSummary.amount.toFixed(2) : null
                    }
                }

                if (response.data.user) {
                    this.user = response.data.user
                    this.totalCompletedTimeToday = response.data.total_time_today;
                }
            }
            
            this.loading = false
        })
    }

    @action STAR = async(order) => {
        await axios.post('/production/toggle-star-state', {
            orderId: order.id
        }).then((response) => {
            if (response.status === 200) {

                let useDate = this.filters.selects.groupBy.selected.value === 'production' ? order.production_date : order.delivery_date;

                if (moment(useDate) < moment().startOf('day')) {
                    useDate = 'late_orders';
                }

                var clickedOrder = this.ordersByDate[useDate].orders.find(o => o.id === order.id);
                clickedOrder.star = response.data.star;
            }
        })
    }

    @action HANDLE_SET_TASKS_ASYNC = async (date, taskposition, orderPosition, userId) => {
        const order = this.ordersByDate[date].orders[orderPosition]
        let update = false

        // Save the last changed data.
        this.taskChanged = {
            date: date,
            orderPosition: orderPosition,
            taskPostion: taskposition
        }

        // Check if we should update all or one task.
        if (taskposition > -1) {
            // Is the task already done? Show popup.
            if (order.tasks[taskposition].done) {
                this.popupOrder.show = true
            } else {
                // The task is not changed, set it to done.
                order.tasks[taskposition].done = moment().format('YYYY-MM-DD')
                update = true
            }
        } else {
            // Is the whole order done?
            if (order.orderDone) {
                // Show popup
                this.popupOrder.show = true
            } else {
                order.tasks.map((task) => task.done = moment().format('YYYY-MM-DD'))
                order.orderDone = true
                update = true
            }
        }
        
        // Update
        if (update) {
            order.order_paused = false
            // Set paused to false

            await axios.post('/production/update-status', {
                orderId: order.id,
                orderDone: order.orderDone,
                tasks: order.tasks,
                paused: order.order_paused,
                userId: userId
            }).then((response) => {
                if (response.status === 200) {
                    this._calculateProgress(this.ordersByDate)
                    this._setSummation(this.ordersByDate)
                    this._workedTimeToday(this.ordersByDate)
                    DashStore.FETCH_RESERVATION()
                }
            })
        }
    }

    @action HANDLE_UNDO_TASK_ASYNC = async (action) => {
        const date = this.taskChanged.date
        const taskPos = this.taskChanged.taskPostion
        const orderPos = this.taskChanged.orderPosition
        const order = this.ordersByDate[date].orders[orderPos] 
        const task = order.tasks[taskPos]

        // Kind of measure
        if (action === 'update') {
            let update = false

            // Don't show comment section
            this.popupOrder.showComment = false
            // Set paused to false
            order.order_paused = false

            // Should we update all tasks or just one?
            if (taskPos > -1) {
                // Undo the task.
                if (task && task.done) {
                    task.done = null
                    order.orderDone = false
                    update = true
                }
            } else {
                // Undo all tasks
                order.tasks.map((task) => task.done = null)
                order.orderDone = false
                update = true
            }
        } else if (action === 'pause') {                   
            this.popupOrder.showComment = true
        }

        if (action === 'update' || action === 'pauseReq') {
            // Wait to undo the task until the actually click
            if (action === 'pauseReq') {
                order.order_paused = true

                if (task) {
                    task.done = null
                }
            }

            await axios.post('/production/update-status', {
                orderId: order.id,
                orderDone: order.orderDone,
                tasks: order.tasks,
                comment: this.currentComment,
                paused: order.order_paused
            }).then((response) => {
                if (response.status === 200) {
                    this.FETCH_ORDERS_ASYNC()
                    this.popupOrder.show = false
                    this.popupOrder.showComment = false
                    this.currentComment = ''
                }
            })        
        }
    }

    @action HANDLE_TOGGLE_TASKS = (order) => {
        order.openTaskBar = !order.openTaskBar

        if (order.openTaskBar) {
            this.openedOrders.push(order)
        } else {
            const index = this.openedOrders.findIndex(o => o.id === order.id)
            this.openedOrders.splice(index, 1)
        }
    }

    @action HANDLE_TOGGLE_DAY = (day) => {        
        day.openDayBar = !day.openDayBar

        if (this.openSetting) {
            if (!day.openDayBar) {
                this.openedDays.push(day)
            } else {
                const index = this.openedDays.findIndex(d => d.date === day.date)            
                this.openedDays.splice(index, 1)
                this.closestDate = null
            }
        } else {
            if (day.openDayBar) {
                this.openedDays.push(day)
            } else {
                const index = this.openedDays.findIndex(d => d.date === day.date)            
                this.openedDays.splice(index, 1)
                this.closestDate = null
            }            
        }        
    }

    @action HANDLE_RESET_FILTER = () => {
        // Clear filter payload/remember array.
        this.selectedFilters = []
        this.ordersPayload.filters = []

        // Walkthrough filters and clear selected to null again.
        for (const filter in this.filters.selects) {
            this.filters.selects[filter].selected = null            
        }        

        // Fetch over the filtered result.
        this.FETCH_ORDERS_ASYNC()
    }

    @action HANDLE_CLOSE_POP_UP = () => {
        this.popupOrder.show = false
        this.popupOrder.showComment = false
        this.currentComment = ''
        this.popup = {
            show: false,
            data: {}
        }
    }

    @action HANDLE_CUSTOMER_POP_UP = (date, orderPosition) => {
        let data = this.ordersByDate[date].orders[orderPosition]

        if (data.cost_estimate && data.cost_estimate.contact) {
            data = data.cost_estimate.contact

            this.popup.data = {
                name: `${ data.firstname } ${ data.lastname }`,
                phone: data.phone,
                cellphone: data.cellphone,
                email: data.email,
                role: data.role,
                note: data.note
            }
        }

        this.popup.show = true
    }

    @action HANDLE_CLOSE_DATE_POP_UP = () => {
        this.popupChangeDate.show = false
        this.popupChangeDate = {
            show: false,
            data: {
                order: '',
                locked: true,
                notify_delivery_date_change: false,
                delivery_change_reason: ''
            }
        }
    }

    @action HANDLE_SAVE_DATE_POP_UP = async() => {

        await axios.post('/production/modify-order-dates', {
            id: this.popupChangeDate.data.order.id,
            production_date: this.popupChangeDate.data.order.production_date,
            delivery_date: this.popupChangeDate.data.order.delivery_date,
            notify_delivery_date_change: this.popupChangeDate.data.notify_delivery_date_change,
            reason: this.popupChangeDate.data.delivery_change_reason
        }).then((response) => {
            if (response.status === 200) {
                this.HANDLE_CLOSE_DATE_POP_UP();
                this.FETCH_ORDERS_ASYNC();
                DashStore.FETCH_RESERVATION();
            }
        })
    }

    @action UNLOCK_ORDER = () => {
        this.popupChangeDate.data.locked = false;
    }

    @action HANDLE_DATE_POP_UP = (order) => {
        if (order) {
            this.popupChangeDate.data.order = order
        }

        this.popupChangeDate.show = true
    }

    @action HANDLE_PRODUCTION_DATE_CHANGE(newValue) {
        let month = (''+newValue.month).padStart(2, '0');
        let day = (''+newValue.day).padStart(2, '0');
        let year = ''+newValue.year;
        this.popupChangeDate.data.order.production_date = year+'-'+month+'-'+day;
    }

    @action HANDLE_DELIVERY_DATE_CHANGE(newValue) {
        let month = (''+newValue.month).padStart(2, '0');
        let day = (''+newValue.day).padStart(2, '0');
        let year = ''+newValue.year;
        this.popupChangeDate.data.order.delivery_date = year+'-'+month+'-'+day;
    }

    @action HANDLE_DATE_RANGE = (range) => {
        this.dateRange = range
        this.ordersPayload.range = range

        if (this.dateRange.to) {
            this.FETCH_ORDERS_ASYNC()
        }
    }

    @action HANDLE_RESET_DATEPICKER = () => {
        this.dateRange = {
            from: null,
            to: null
        }
        this.ordersPayload.range = this.dateRange

        this.FETCH_ORDERS_ASYNC()
    }

    @action HANDLE_ADD_ORDER_COMMENT = (event) => {
        event.persist()
        
        const { value } = event.target
        const date = this.taskChanged.date
        const orderPos = this.taskChanged.orderPosition
        const order = this.ordersByDate[date].orders[orderPos] 

        order.comment = value
        this.currentComment = value
    }

    /**
     * GLOBALS
     */

    _setOrders = (response) => {        
        // Always clear current data before adding new.
        this.ordersByDate = {}    
        
        if (response.length > 0) {
            const temp = {}

            response.forEach((order) => {
                let filterDate = this.filters.selects.groupBy.selected.value === 'production' ? moment(order.production_date) : moment(order.delivery_date)
                let date = filterDate.format('YYYY-MM-DD')
                
                // Create days if not exists
                if (order.is_late && !Object.keys(temp).includes('late_orders')) {
                    temp['late_orders'] = {
                        day: filterDate.format('dddd'),
                        date: date,
                        orders: [],
                        totalValuePerDay: 0,
                        totalTimeAllOrders: 0,
                        totalDayPercentile: 0,
                        totalPerformedTime: '00:00',
                        openDayBar: this.openSetting ? this.openSetting : this.openedDays.find(o => o.date === date) !== undefined ? true : false,
                        today: false
                    }

                    if (this.openSetting && this.openedDays.some(el => el.date === temp['late_orders'].date)) {
                        temp['late_orders'].openDayBar = false
                    }
                } else if (!Object.keys(temp).includes(date) && !order.is_late) {
                    temp[date] = {
                        day: filterDate.format('dddd'),
                        date: date,
                        orders: [],
                        totalValuePerDay: 0,
                        totalTimeAllOrders: 0,
                        totalDayPercentile: 0,
                        totalPerformedTime: '00:00',
                        openDayBar: this.openSetting ? this.openSetting : this.openedDays.find(o => o.date === date) !== undefined ? true : false,
                        today: false
                    }

                    if (this.openSetting && this.openedDays.some(el => el.date === temp[date].date)) {
                        temp[date].openDayBar = false
                    }
                }
                
                // Just push the data we actually need to the current day
                temp[(order.is_late ? 'late_orders' : date)].orders.push({
                    id: order.id,
                    order_date: order.order_date,
                    tasks: order.rows,
                    cost_estimate: order.cost_estimate,                        
                    delivery_company: order.delivery_company,
                    filterDate: date,
                    production_date: order.production_date,
                    delivery_date: order.delivery_date,
                    production_info: order.production_info,
                    quantity: order.quantity,
                    orderDone: order.done,
                    openTaskBar: this.openedOrders.filter(o => o.id === order.id).length > 0 ? true : false,
                    value_amount: order.value_amount,
                    value_amount_display: formatNumber(order.value_amount),
                    totalProductionTimeDay: '',
                    status: '',
                    star: order.star,
                    pdf_file: order.pdf_file,
                    performedTime: 0,
                    order_paused: order.paused,
                    shipper: order.shipper,
                    comment: order.comment,
                    customer: Object.keys(order.contact.customer).length > 0 ? order.contact.customer : null
                })
            })            

            // Get closest date from today            
            if (!this.openSetting && this.ajaxFirstReq) {
                if (Object.keys(temp).length > 0) {
                    Object.keys(temp).forEach((date) => {                    
                        if (date !== 'late_orders') {
                            if (this.openedDays.length < 2) {
                                temp[date].openDayBar = true                            
                                this.openedDays.push(temp[date])
                            }
                        } else {
                            temp['late_orders'].openDayBar = true
                            this.openedDays.push(temp[date])
                        }
                    })    
                } 

                this.ajaxFirstReq = false
            }
            
            // Calculate all orders of days.
            this._calculateProgress(temp)

            // All done, update state.
            this.ordersByDate = temp

            // Check todays performed time
            this._workedTimeToday(this.ordersByDate)
        }

        // Set summation
        this._setSummation(this.ordersByDate)

        // Set last current position
        window.scroll(0, this.scrollTop)
    }

    _calculateProgress = (orders) => {
        // Each day
        for (const day in orders) {
            const productionTotalTime = []
            const totalRemainingTime = []
            const ordersLength = orders[day].orders.length
            const ordersLeft = orders[day].orders.filter(o => o.orderDone !== 1 && o.orderDone !== true).length            
            let totalValue = 0

            // Each order
            orders[day].orders.forEach((order) => {
                const remainingTime = []
                const productionTime = []
                const performedTime = []

                // Each task
                if (order.tasks.length > 0) {                    
                    order.tasks.forEach((task) => {
                        if (task.article !== null) {
                            // Get total prod. time for each task.
                            let taskProdTime = task.article.production_time

                            if (task.article.infoboard_id && task.article.infoboard_id == 6) {
                                const infoboardData = JSON.parse(task.infoboarddata)
                                if (infoboardData.production_time) {
                                    taskProdTime = infoboardData.production_time
                                }
                            }

                            const prodTime = getTotalProductionTime(task.article.start_time, taskProdTime, task.quantity)
                            // If done true, set time as 00:00:00 else select task total time.
                            const remTime = task.done ? '00:00' : prodTime
                            
                            // Push it.
                            remainingTime.push(remTime)
                            productionTime.push(prodTime)                        

                            if (task.done && prodTime !== '00:00') {
                                performedTime.push(prodTime)
                            }

                            // Create a new property that displays the task's total time and that we don't have in the calculations.
                            task.article.prod_display_time = prodTime;// moment(prodTime, 'HH:mm').format('HH:mm')
                            // Task format remaining time
                            task.remaining_display_time  = remTime;// moment(remTime, 'HH:mm').format('HH:mm')
                            // Set remaining as 00:00:00 or total time for each tasks.
                            task.remainingTime = remTime

                            // Include unit and limit the quantity to 2 decimals only.
                            task.quantity = `${ parseFloat(task.quantity).toFixed(2) } ${ task.article.unit }`                                                                          

                            // Push and save articles filter to be prepared
                            if (!this.filterTemp.some(f => f.article_number === task.article.article_number)) {
                                this.filterTemp.push({
                                    article_number: task.article.article_number,
                                    id: task.article.id,
                                    label: task.article.name,
                                    value: task.article.name,
                                    type: 'articles'
                                })                                
                            }                    
                        }
                    })
                }

                order.remainingTime = countTotalTime(remainingTime, 'HH:mm:ss')
                order.remainingTime_display = countTotalTime(remainingTime, 'HH:mm')

                order.orderTotalTime = countTotalTime(productionTime, 'HH:mm:ss')
                order.orderTotalTime_display = countTotalTime(productionTime, 'HH:mm')
                
                order.performedTime = countTotalTime(performedTime, 'HH:mm:ss')

                const remainingTimeSeconds = moment.duration(order.remainingTime).asSeconds()
                const productionTimeSeconds = moment.duration(order.orderTotalTime).asSeconds()
                
                if (remainingTimeSeconds > 0) {
                    order.orderPercentile = Math.ceil((remainingTimeSeconds / productionTimeSeconds) * 100)
                } else {
                    order.orderPercentile = 0
                }                
                
                // Totals
                productionTotalTime.push(order.orderTotalTime)
                totalValue += parseFloat(order.value_amount)
                totalRemainingTime.push(order.remainingTime)            
            })

            // Save Articles filters
            this.filters.selects.articles.options = this.filterTemp

            // Totel % per day
            orders[day].totalDayPercentile = ((100 * ordersLeft) / ordersLength).toFixed(0);
            // Total time
            orders[day].productionTotalTime = countTotalTime(productionTotalTime, 'HH:mm')
            // Total value per day
            orders[day].totalValuePerDay = formatNumber(parseInt(parseFloat(totalValue) * 100) / 100)
            // Count orders per day
            orders[day].totalOrdersPerDay = orders[day].orders.length
            // Total remaining time
            orders[day].totalRemainingTime = countTotalTime(totalRemainingTime, 'HH:mm')
            // Check order progress
            this._checkOrderProgress(orders[day].productionTotalTime, orders[day].orders)
        }
    }

    _workedTimeToday = (orders) => {
        const isOrderToday = moment().format('YYYY-MM-DD')
        const totalperformedTimeToday = []
        
        for (const orderKey in orders) {
            orders[orderKey].orders.map((order) => {
                if (order.performedTime !== '00:00:00') {
                    totalperformedTimeToday.push(order.performedTime)
                }
            })
        }

        
        if (Object.keys(this.ordersByDate).includes(isOrderToday)) {
            this.ordersByDate[isOrderToday].today = true

            if (totalperformedTimeToday.length > 0) {
                this.ordersByDate[isOrderToday].totalPerformedTime = countTotalTime(totalperformedTimeToday, 'HH:mm')
            } else {
                this.ordersByDate[isOrderToday].totalPerformedTime = '00:00'
            }
        }
    }

    _checkOrderProgress = (productionTime, orders) => {
        productionTime = moment.duration(productionTime).asSeconds()

        if (orders.length > 0) {
            orders.forEach((order) => {
                let orderPercentileTotal = 0
                let taskDone = 0
                const orderTotal = moment.duration(order.orderTotalTime).asSeconds()

                // Check Tasks
                if (order.tasks.length > 0) {
                    order.tasks.forEach((task) => {
                        const taskTime = moment.duration(task.remainingTime).asSeconds()

                        if (task.done) {
                            taskDone++
                        }
                        
                        if (taskTime > 0) {
                            const taskPercentile = ((taskTime / orderTotal) * 100).toFixed(2)
                            orderPercentileTotal += parseFloat(taskPercentile)

                            task.taskPercentile = taskPercentile
                        } else {
                            task.taskPercentile = 0
                        }
                    })                                
                }

                order.orderPercentileTotal = Math.round(orderPercentileTotal)

                /**
                 * Set status for each order
                 * 
                 * Green = Klar
                 * Blue = Ej påbörjad
                 * Yellow = Påbörjad
                 * Orange = Försenad
                 * Red = Ej levererad
                 */

                if (order.order_paused) {
                    order.status = 'paused'
                } else {
                    if (moment().isAfter(order.production_date, 'day') && taskDone < 1) {
                        order.status = 'delayed' // (orange)
    
                        if (taskDone >= order.tasks.length && order.orderDone) {
                            order.status = 'done' // (green)
                        }
                    } else {
                        if (moment().isAfter(order.delivery_date, 'day')) {
                            order.status = 'delayed-delivered' // (red)
    
                            if (taskDone >= order.tasks.length && order.orderDone) {
                                order.status = 'done' // (green)
                            }
                        } else {                        
                            if (taskDone <= 0) {
                                order.status = 'not-started' // (blue)
                            } else if (taskDone > 0 && taskDone < order.tasks.length) {
                                order.status = 'started' // (yellow)
                            } else if (taskDone >= order.tasks.length && order.orderDone) {
                                order.status = 'done' // (green)
                            } else if (taskDone >= order.tasks.length && !order.orderDone) {
                                order.status = 'started' // (yellow)
                            }
                        }
                    }                
                }                
            })
        }
    }

    _setSummation = (orders) => {
        const data = Object.keys(orders)
        let sum = {
            productionTime: '',
            remainingTime: '',
            orders: 0,
            value: 0,
            totalPercentile: 0
        }      
        
        if (data.length > 0) {
            let totalProdTime = 0
            let totalRemTime = 0
            let totalPerformedTime = 0
            let value = 0            

            data.forEach((date) => {
                // Total prod time
                totalProdTime += moment.duration(orders[date].productionTotalTime).asSeconds()

                // Total rem. time
                totalRemTime += moment.duration(orders[date].totalRemainingTime).asSeconds()

                // Total perf. time
                totalPerformedTime += moment.duration(orders[date].totalPerformedTime).asSeconds()
                
                // Total orders
                sum.orders += orders[date].totalOrdersPerDay

                // Total value
                value += parseInt(orders[date].totalValuePerDay.replace(/\s/g, ''))
                
                // Total percentile            
                // const orderPercentile = (100 / data.length * (parseInt(orders[date].totalDayPercentile) / 100))
                // sum.totalPercentile += parseFloat(orderPercentile)
            })

            sum.productionTime = countTotalTime(totalProdTime, 'HH:mm')
            sum.remainingTime = countTotalTime(totalRemTime, 'HH:mm')
            sum.performedTime = countTotalTime(totalPerformedTime, 'HH:mm')
            sum.value = formatNumber(value)

            sum.totalPercentile = ((totalRemTime/totalProdTime) * 100).toFixed(0); ///sum.totalPercentile.toFixed(0)
        }

        // Insert
        this.orderSummation = sum
    }
}

const store = new Store()
export default store 